import { type Key } from 'react';

import { IconButton, ListItemIcon, ListItemText, Box } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { tss } from 'tss-react/mui';

import { GroupOrServiceAvatar } from '@/components/Avatar/GroupOrServiceAvatar';
import { Or2ListItem } from '@/components/data-display/Or2ListItem';
import { KeyBadge } from '@/components/KeyBadge/KeyBadge';

import type { FilterGroup } from './MultiGroupFilter';

const useStyles = tss.create(({ theme }) => ({
  ksgBadge: {
    border: `2px solid ${theme.palette.background.default}`,
    backgroundColor: theme.palette.background.default,
  },
  badge: {
    marginRight: theme.spacing(2),
  },
}));

interface MultiGroupFilterGroupOptionProps {
  option: FilterGroup;
  selected: boolean;
  disabled?: boolean;
  // Shown next to the group name when the option is disabled
  disabledMessage?: string;
  hasKsg?: boolean | null;
  props: React.HTMLAttributes<HTMLLIElement> & { key?: Key };
}

export const MultiGroupFilterGroupOption = ({
  option,
  selected,
  disabled = false,
  disabledMessage,
  hasKsg,
  props,
}: MultiGroupFilterGroupOptionProps) => {
  const { classes } = useStyles();
  const { key, onClick, ...rest } = props;

  const handleClick = (event: React.MouseEvent<HTMLLIElement>) => {
    if (disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick?.(event);
  };

  return (
    <Or2ListItem
      dense
      key={`${option._id}-${key}`}
      onClick={handleClick}
      sx={{
        ...(disabled && {
          opacity: 0.6,
          cursor: 'not-allowed',
          pointerEvents: 'auto',
        }),
      }}
      secondaryAction={
        <IconButton disabled={disabled}>
          {selected ? (
            <CheckBoxIcon
              fontSize="small"
              color={disabled ? 'disabled' : 'primary'}
            />
          ) : (
            <CheckBoxOutlineBlankIcon
              fontSize="small"
              color={disabled ? 'disabled' : 'primary'}
            />
          )}
        </IconButton>
      }
      {...rest}
    >
      <ListItemIcon>
        <KeyBadge
          keyAvatarProps={{ size: 12, className: classes.ksgBadge }}
          className={classes.badge}
          hasKsg={hasKsg ?? false}
        >
          <GroupOrServiceAvatar
            iconUrl={option?.iconUrl}
            groupOrServiceName={option.name}
            size={32}
          />
        </KeyBadge>
      </ListItemIcon>
      <ListItemText
        primary={
          <Box component="span" display="flex" alignItems="center" gap={1}>
            <span>{option?.name}</span>
            {disabled && (
              <Box
                component="span"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  fontStyle: 'italic',
                }}
              >
                {disabledMessage}
              </Box>
            )}
          </Box>
        }
        slotProps={{ primary: { variant: 'subtitle1' } }}
      />
    </Or2ListItem>
  );
};
